<template>
    <div v-if="show" class="send_otp_container">
        <div class="method_selection">
            <p class="method_title">{{ $t('common.getCode') }}</p>
            <p class="method_title">{{ $t('common.codeWillSend') }}
               <span v-if="stepType == 'email'">{{ mask(props.verificationData.email) }}</span>
               <span v-if="stepType == 'phone'">{{ mask(props.verificationData.phoneNumber) }}</span>
            </p>
            
            <!-- Captcha for Step 2 -->
            <CaptchaField 
                :captcha-img="captchaData?.img ? 'data:image/png;base64,' + captchaData.img : ''"
                @valueChanged="updateOtpCaptcha"
                @updateCaptcha="getCaptcha" />
            
            <InputField v-model="otpValue" @valueChanged="smsOTPChange" ref="smsOTPRef"
                :labelText="$t('loginRegister.smsCode')" fieldType="number" :isOTP="true" @getOtp="sendOtp" />

            <!-- Send OTP Button -->
            <MainButton :title="$t('common.verificationOTP')" radius="5px !important"
                :activeStatus="otpCaptcha.length > 0 && otpValue.length > 0" @click="verifyOtp"
                btn-padding="30px !important" style="margin: 2rem 0 1rem;" :isBtnLoading="btnLoading" />
        </div>
    </div>
    <StatusPopup ref="statusRef" :is-show-success="isSuccess" :content="toastContent" />
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import InputField from './inputField.vue';
import CaptchaField from '../captchaField.vue';
import { sendOtpCode, verifyOtpCode, getCaptchaCode } from '@/api/user';
import { useI18n } from 'vue-i18n';
import MainButton from '../../../components/common/mainButton.vue';
import StatusPopup from '@/components/common/statusPopup.vue';

const { t } = useI18n();
const statusRef = ref()
const isSuccess = ref(false)
const toastContent = ref('')
const smsOTPRef = ref()
const btnLoading = ref(false)
const props = defineProps({
    verificationData: {
        type: Object,
        default: () => ({})
    },
    phoneNumber: {
        type: String,
        default: ''
    },
    uuid: {
        type: String,
        default: ''
    },
    emailVerified: {
        type: Boolean,
        default: false
    },
    phoneVerified: {
        type: Boolean,
        default: false
    },
    stepType: {
        type: String,
        default: 'SMS'
    }
});

const emit = defineEmits(['verified', 'close']);

const show = ref(true);
const selectedMethod = ref('');
const contactValue = ref('');
const phoneNumberDisplay = ref(props.phoneNumber);
const otpCaptcha = ref('');
const otpValue = ref('');
const captchaData = ref<any>(null);
const updateOtpCaptcha = (val: string) => {
    otpCaptcha.value = val;
};
const contactType = computed(() => {
  if (props.stepType === 'phone') {
    return props.verificationData.whatsappVerified ? 'WHATSAPP' : 'SMS';
  }
  return 'EMAIL';
});
const areaCode = ref('55'); // Brazil area code

const pendingMethods = ref<string[]>([]);
const currentMethodIndex = ref(0);

const getCaptcha = () => {
    getCaptchaCode().then(res => {
        captchaData.value = res.data;
    });
};

watch(() => props.phoneNumber, (newVal) => {
    phoneNumberDisplay.value = newVal;
});

// Initialize selected method based on verification data
watch(() => props.verificationData, (data) => {
    const methods: string[] = [];
    if (data?.smsVerified) methods.push('sms');
    if (data?.whatsappVerified) methods.push('WHATSAPP');
    if (data?.emailVerified) methods.push('email');
    pendingMethods.value = methods;
    currentMethodIndex.value = 0;
    if (methods.length > 0) {
        selectedMethod.value = methods[0];
    }
    getCaptcha();
}, { immediate: true });

const sendOtp = () => {
    if (!selectedMethod.value){
        smsOTPRef.value?.loadingFalse();
        return;
    }
    if (selectedMethod.value === 'email' && !contactValue.value){
        smsOTPRef.value?.loadingFalse();
        return;
    }
    const captcha = otpCaptcha.value?.trim();
    if (!captcha) {
        smsOTPRef.value?.loadingFalse();
        return;
    }
    const contact = props.stepType === 'email' ? props.verificationData.email : props.verificationData.phoneNumber;
    const params = {
        uuid: captchaData.value?.uuid,
        fpCaptchaType: 'CAPTCHA-FP-step-2',
        captcha: otpCaptcha.value.trim(),
        forgetPasswordCaptcha: captchaData.value?.forgetPasswordCaptchaEnabled,
        contact: contact,
        contactType: contactType.value,
        areaCode: areaCode.value,
        fpUuid: props.verificationData.fpUuid
    };

    sendOtpCode(params)
        .then(res => {
            if (res.code === 200) {
                smsOTPRef.value?.loadingFalse();
                smsOTPRef.value?.startCountdown();
                showSuccess(res.msg);
            } else {
                getCaptcha();
                smsOTPRef.value?.loadingFalse();
                showError(res.msg);
            }
        })
        .catch(err => {
            showError(err);
            getCaptcha();
            smsOTPRef.value?.loadingFalse();
        });
};

const verifyOtp = () => {
    if (!otpValue.value || !otpValue.value.trim()) {
        return;
    }
    const contact = props.stepType === 'email' ? props.verificationData.email : props.verificationData.phoneNumber;
    const params = {
        contact: contact,
        contactType: contactType.value,
        otpCode: otpValue.value.trim(),
        areaCode: areaCode.value,
        fpUuid: props.verificationData.fpUuid
    };
    btnLoading.value = true;
    verifyOtpCode(params)
        .then(res => {
            if (res.code === 200) {
                showSuccess(res.msg);
                setTimeout(() => {
                    emit('verified', props.stepType);
                }, 1000);
            } else {
                showError(res.msg);
            }
            resendOtp();
            btnLoading.value = false;
        })
        .catch(err => {
            console.error('Verify OTP error:', err);
            btnLoading.value = false;
            showError(err);
            // alert('OTP verification failed. Please try again.');
        });
};

const resendOtp = () => {
    otpValue.value = '';
    otpCaptcha.value = '';
    otpValue.value = '';
    getCaptcha();
};

const mask = (v: string) =>
  v?.includes('@')
    ? v.replace(/^(.{1,2}).*(@.*)$/, (_, a, b) => a + '*'.repeat(Math.max(v.split('@')[0].length - a.length, 1)) + b)
    : v?.replace(/^(.{3}).*(.{4})$/, (_, a, b) => a + '*'.repeat(v.length - 7) + b) || '';

const showError = (message: string) => {
    toastContent.value = message;
    isSuccess.value = false;
    statusRef.value.open()
}

const showSuccess = (message: string) => {
    toastContent.value = message;
    isSuccess.value = true;
    statusRef.value.open()
}

const smsOTPChange = (value: string) => {
    // emits('smsOTPChange', value)
}

</script>

<style scoped>
.send_otp_container {
    padding: 20px 0;
}

.method_selection, .verify_otp_section {
    margin-bottom: 20px;
}

.method_title, .verify_title {
    color: #ffffff66;
    font-size: 15px;
    margin: 3px;
    font-weight: 500;
}

.verify_contact {
    color: #22c1b0;
    font-size: 14px;
    margin-bottom: 20px;
    font-weight: 500;
}

.method_option {
    display: flex;
    align-items: center;
    padding: 15px 12px;
    margin-bottom: 12px;
    border-radius: 5px;
    background: rgba(34, 193, 176, 0.1);
    cursor: pointer;
    transition: background 0.3s;
    border: 1px solid rgba(34, 193, 176, 0.2);
}

.method_option:hover {
    background: rgba(34, 193, 176, 0.2);
    border-color: rgba(34, 193, 176, 0.4);
}

.method_option input[type="radio"] {
    margin-right: 12px;
    cursor: pointer;
}

.method_option span {
    color: #fff;
    font-size: 14px;
}

.send_otp_container :deep(.input_container) {
    margin-bottom: 15px;
    margin-top: 15px;
}

.send_otp_btn, .verify_btn, .resend_btn {
    background: linear-gradient(90deg, rgb(1, 73, 72) -18%, rgb(15, 177, 175) 122%);;
    color: #fff;
    border: none;
    padding: 12px 24px;
    border-radius: 5px;
    margin-top: 20px;
    margin-right: 10px;
    margin-bottom: 10px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: background 0.3s;
    width: 100%;
}

.send_otp_btn:hover, .verify_btn:hover {
    background: #1ba89a;
}

.resend_btn {
    background: rgba(34, 193, 176, 0.5);
    width: auto;
    margin-right: 10px;
}

.resend_btn:hover {
    background: rgba(34, 193, 176, 0.7);
}

.success_message {
    text-align: center;
    padding: 30px 20px;
    border-radius: 10px;
    background: rgba(34, 193, 176, 0.1);
}

.success_message p {
    color: #22c1b0;
    font-size: 14px;
    margin: 10px 0;
    font-weight: 500;
}
</style>
